import { gql } from '@apollo/client';
import { MediaQueries, BillingQueries, ProfileQueries } from '@minecraft.shared-queries';
import { ProfileMediaFragment } from './media';

export const MUTATION_LOGIN = gql`
  mutation login($email: String!, $password: String!) {
    token: login(email: $email, password: $password)
  }
`;

export const SET_USER_TOKEN = gql`
  mutation setUserToken($token: String!, $isLoggedIn: Boolean!) {
    setUserToken(token: $token, isLoggedIn: $isLoggedIn) @client
  }
`;

export const IS_LOGGED_IN = gql`
  query IsUserLoggedIn {
    token @client
    isLoggedIn @client
  }
`;

export const MUTATION_CREATE_USER = gql`
  mutation createAccount(
    $firstName: String!
    $lastName: String!
    $email: String!
    $password: String!
    $countryId: ID!
  ) {
    token: createAccount(
      input: { firstName: $firstName, lastName: $lastName, email: $email, password: $password, countryId: $countryId }
    )
  }
`;

export const ACCOUNT_COUNTRIES_FETCH = gql`
  query countries {
    countries {
      name
      id
      code
      enabled
      sortOrder
    }
  }
`;

export const PROFILE_CREATE = gql`
  mutation createProfile($input: ProfileCreateInput!) {
    createProfile(input: $input) {
      message
    }
  }
`;

export const MUTATION_CREATE_PROFILE = gql`
  mutation createProfile($stageName: String!, $minor: Boolean!, $accountId: Float, $profileStatusId: Int) {
    createProfile(
      input: { stageName: $stageName, minor: $minor, accountId: $accountId, profileStatusId: $profileStatusId }
    ) {
      profileId
      profileStatus {
        id
        code
        name
      }
    }
  }
`;

export const MUTATION_UPDATE_PROFILE = gql`
  mutation updateProfile($input: ProfileUpdateInput!) {
    updateProfile(input: $input) {
      __typename
      profileId
      profileStatus {
        __typename
        id
        code
        name
      }
    }
  }
`;

export const PROFILE_CREATE_FETCH = gql`
  query profileCreateFetch {
    dmas {
      id
      name
      code
      region {
        id
        name
        code
        country {
          id
          code
          name
          continent {
            id
          }
        }
      }
    }
    countries {
      id
      code
      name
      continent {
        id
      }
    }
    heights {
      id
      METRIC_CENTIMETERS
      IMPERIAL_INCHES
    }
    weights {
      id
      IMPERIAL_LBS
      METRIC_KG
    }
    seekingRepresentations {
      id
      name
    }
    hasRepresentation
    genderAppearances {
      id
      name
      code
    }
    genderIdentities {
      id
      name
      code
    }
    ethnicAppearances {
      id
      name
      code
      sortOrder
    }
    willingnesses {
      id
      code
      name
      parentObject {
        id
        name
        code
      }
      childObjects {
        id
        name
        code
      }
    }
    pursuings {
      code
      name
      id
    }
    talentUnions {
      id
      name
      code
      sortOrder
      parentObject {
        id
        code
        name
        sortOrder
      }
    }
  }
`;

export const GET_PROFILE_TYPE = gql`
  {
    isAdultProfile @client
  }
`;

export const PUBLIC_PROFILE_DETAIL = gql`
  query getPublicProfile(
    $guid: String!
    $roleId: Float!
    $folderId: Float!
    $showSummaryWithSubmissionDetails: Boolean!
    $showSummaryWithFolderCustomization: Boolean! = false
  ) {
    publicProfile(guid: $guid) {
      id
      profileId
      stageName
      blurb
      otherExperiences
      hideCreditYear
      defaultSubmissionNote
      submission(roleId: $roleId) @include(if: $showSummaryWithSubmissionDetails) {
        id
        note
        requestedMedia {
          ...SubmissionMedia
        }
        media {
          ...SubmissionMedia
          isLocked
        }
        workingLocation {
          id
          name
          sortOrder
        }
      }
      folderCustomization(folderId: $folderId) @include(if: $showSummaryWithFolderCustomization) {
        id
        note
        isOverscale
        overscaleNote
        media {
          id
          isPrimary
          name
          order
          media {
            ...MediaAsset
          }
        }
      }
      isPersonal @include(if: $showSummaryWithSubmissionDetails)
      profileMainDivision {
        id
        name
        parentName
        logo {
          id
          fileKey
        }
      }
      profileMainOrganization @include(if: $showSummaryWithSubmissionDetails) {
        id
        name
      }
      profileAccountCountry {
        id
        code
      }
      profileStatus {
        id
        code
        name
      }
      mediaAssets {
        ...ProfileMediaValues
      }
      height {
        id
        IMPERIAL_FEET_INCHES
        METRIC_CENTIMETERS
      }
      weight {
        id
        IMPERIAL_LBS
        METRIC_KG
        kgRounded
      }
      waist {
        id
        inch
        cm
        inchRounded
      }
      inseam {
        id
        inch
        cm
        inchRounded
      }
      gloveSize {
        id
        inch
        cm
        inchRounded
      }
      hatSize {
        id
        inch
        cm
      }
      profileTshirtSizes {
        id
        name
        category {
          id
          name
        }
      }
      profileShoeSizes {
        id
        shoeSizeUS
        shoeSizeUK
        shoeSizeEU
        shoeSizeAU
        category {
          id
          name
        }
        shoeModifier {
          id
          name
        }
      }
      dressSize {
        id
        sizeUS
        sizeUK
        sizeEU
        sizeAU
      }
      bust {
        id
        inch
        cm
        inchRounded
      }
      underbust {
        id
        inch
        cm
        inchRounded
      }
      hipSize {
        id
        inch
        cm
        inchRounded
      }
      cupSize {
        id
        name
      }
      profilePants {
        id
        sizeUS
        sizeUK
        sizeEU
        sizeAU
        gender {
          id
          code
          name
        }
      }
      chestSize {
        id
        inch
        cm
        inchRounded
      }
      sleeve {
        id
        inch
        cm
        inchRounded
      }
      neck {
        id
        inch
        cm
        inchRounded
      }
      coat {
        id
        name
      }
      profileSizes {
        id
        name
        category {
          id
          name
        }
        sizeModifier {
          id
          name
        }
      }
      workingLocations {
        id
        name
        sortOrder
      }
      talentUnions {
        id
        name
        code
        sortOrder
        parentObject {
          id
          code
          name
          sortOrder
        }
      }
      skills {
        id
        code
        name
        profileMediaId
        parentObject {
          id
          code
          name
          parentObject {
            id
            code
            name
          }
        }
        level {
          id
          name
        }
      }
      resume {
        media {
          ...MediaAsset
        }
      }
      credits {
        id
        year
        title
        role
        director
        highlighted
        sortOrder
        creditOrder
        profileCreditTypeId
        creditType {
          id
          code
          name
        }
        creditMedia {
          ...MediaAsset
        }
      }
      socialMedia {
        ...ProfileSocialMedia
      }
    }
  }
  ${ProfileMediaFragment}
  ${MediaQueries.mediaAssetFragment}
  ${MediaQueries.submissionMediaFragment}
  ${ProfileQueries.SOCIAL_MEDIA_FRAGMENT}
`;

export const profileFragment = gql`
  fragment ProfileDetails on Profile {
    __typename
    id
    accountId
    profileId
    stageName
    talentEmail
    phone
    blurb
    otherExperiences
    hideCreditYear
    artistId
    managerEmail
    contactInfo {
      __typename
      phone
      email
    }
    isOverscaled
    overscaleNote
    defaultSubmissionNote
    ageInYears
    guid
    workPermitExp
    profileMainDivision {
      __typename
      id
      name
      parentName
      logo {
        __typename
        id
        fileKey
      }
    }
    profileBookOuts {
      __typename
      profileBookoutId
      startDate
      endDate
    }
    multipleBirth {
      __typename
      id
      name
    }
    mediaAssets {
      ...ProfileMediaValues
    }
    billingFeatures {
      ...BillingFeatures
    }
    height {
      __typename
      id
      IMPERIAL_FEET_INCHES
      METRIC_CENTIMETERS
    }
    weight {
      __typename
      id
      IMPERIAL_LBS
      METRIC_KG
      kgRounded
    }
    waist {
      __typename
      id
      inch
      cm
      inchRounded
    }
    inseam {
      __typename
      id
      inch
      cm
      inchRounded
    }
    gloveSize {
      __typename
      id
      inch
      cm
      inchRounded
    }
    hatSize {
      __typename
      id
      inch
      cm
    }
    profileTshirtSizes {
      __typename
      id
      name
      category {
        __typename
        id
        name
      }
    }
    profileShoeSizes {
      __typename
      id
      shoeSizeUS
      shoeSizeUK
      shoeSizeEU
      shoeSizeAU
      category {
        __typename
        id
        name
      }
      shoeModifier {
        __typename
        id
        name
      }
    }
    dressSize {
      __typename
      id
      sizeUS
      sizeUK
      sizeEU
      sizeAU
    }
    bust {
      __typename
      id
      inch
      cm
      inchRounded
    }
    underbust {
      __typename
      id
      inch
      cm
      inchRounded
    }
    hipSize {
      __typename
      id
      inch
      cm
      inchRounded
    }
    cupSize {
      __typename
      id
      name
    }
    profilePants {
      __typename
      id
      sizeUS
      sizeUK
      sizeEU
      sizeAU
      gender {
        __typename
        id
        code
        name
      }
    }
    chestSize {
      __typename
      id
      inch
      cm
      inchRounded
    }
    sleeve {
      __typename
      id
      inch
      cm
      inchRounded
    }
    neck {
      __typename
      id
      inch
      cm
      inchRounded
    }
    coat {
      __typename
      id
      name
    }
    profileSizes {
      __typename
      id
      name
      category {
        __typename
        id
        name
      }
      sizeModifier {
        __typename
        id
        name
      }
    }
    playableAgeMin
    playableAgeMax
    playableAge
    profileGenderIdentities {
      __typename
      genderIdentity {
        __typename
        id
        code
        name
      }
      genderOtherIdentityNote
    }
    genderAppearances {
      __typename
      id
      code
      name
    }
    ethnicAppearances {
      __typename
      id
      name
      order
    }
    hairColor {
      __typename
      id
      name
    }
    willingness {
      __typename
      id
      code
      name
      childObjects {
        __typename
        id
        name
        code
      }
    }
    hairStyle {
      __typename
      id
      name
    }
    facialHair {
      __typename
      id
      name
    }
    eyeColor {
      __typename
      id
      name
    }
    tattoo {
      __typename
      id
      name
    }
    tattooNote
    profilePiercings {
      __typename
      id
      name
    }
    piercingNote
    pursuing {
      __typename
      id
      name
    }
    uniqueAttributes {
      __typename
      id
      name
    }
    workingLocations {
      __typename
      id
      name
      sortOrder
    }
    seekingRepresentations {
      __typename
      id
      name
    }
    hasRepresentation
    profession
    pregnancy
    profileMedia {
      __typename
      media {
        ...MediaAsset
      }
    }
    talentUnions {
      __typename
      id
      name
      code
      sortOrder
      parentObject {
        __typename
        id
        code
        name
        sortOrder
      }
    }
    skills {
      __typename
      id
      code
      name
      profileMediaId
      parentObject {
        __typename
        id
        code
        name
        parentObject {
          __typename
          id
          code
          name
        }
      }
      level {
        __typename
        id
        name
      }
    }
    profileVehicles {
      __typename
      profileVehicleId
      vehicleName
      vehicleYear
      vehicleTypeMake {
        __typename
        id
        make {
          __typename
          id
          name
        }
      }
    }
    profilePets {
      __typename
      id
      name
    }
    props {
      __typename
      id
      name
      profilePropId
    }
    sportEquipments {
      __typename
      id
      name
    }
    profileWardrobes {
      __typename
      id
      name
    }
    documentTypes {
      __typename
      id
      name
    }
    resume {
      __typename
      media {
        ...MediaAsset
      }
    }
    dateOfBirth
    credits {
      __typename
      id
      year
      title
      role
      director
      highlighted
      sortOrder
      creditOrder
      profileCreditTypeId
      creditType {
        __typename
        id
        code
        name
      }
      creditMedia {
        ...MediaAsset
      }
    }
  }
  ${ProfileMediaFragment}
  ${MediaQueries.mediaAssetFragment}
  ${BillingQueries.billingItemsFragment}
`;

export const PROFILE_DETAIL = gql`
  query PROFILE_DETAIL($ids: ID!) {
    profiles(ids: [$ids]) {
      __typename
      ...ProfileDetails
      notes {
        __typename
        id
        comment
        created
        noteModifier {
          __typename
          id
          code
        }
      }
      conflicts {
        __typename
        profileConflictId
        conflictId
        expiration
        note
      }
      socialMedia {
        ...ProfileSocialMedia
      }
    }
  }
  ${profileFragment}
  ${ProfileQueries.SOCIAL_MEDIA_FRAGMENT}
`;

export const TALENT_PROFILE_DETAIL = gql`
  query TALENT_PROFILE_DETAIL($ids: ID!) {
    profiles(ids: [$ids]) {
      ...ProfileDetails
      socialMedia {
        ...ProfileSocialMedia
      }
    }
  }
  ${profileFragment}
  ${ProfileQueries.SOCIAL_MEDIA_FRAGMENT}
`;

export const REMOVE_PROFILE_RESUME_MUTATION = gql`
  mutation removeProfileResume($profileId: Int!) {
    removeProfileResume(profileId: $profileId)
  }
`;

export const SET_PROFILE_RESUME_MUTATION = gql`
  mutation setProfileResume($mediaId: Int!, $profileId: Int!) {
    setProfileResume(mediaId: $mediaId, profileId: $profileId) {
      mediaId
    }
  }
`;

export const ATTACH_MEDIA_TO_PROFILE = gql`
  mutation attachMediaToProfile($input: AttachMediaToProfileInput!) {
    attachMediaToProfile(input: $input) {
      ...BaseMediaWithThumbnail
    }
  }
  ${MediaQueries.baseMediaWithThumbnailFragment}
`;

export const UPDATE_PROFILE_MEDIA_MUTATION = gql`
  mutation updateProfileMedia($profileMedia: ProfileMediaInput!) {
    updateProfileMedia(profileMedia: $profileMedia)
  }
`;

export const DELETE_PROFILE_MEDIA_MUTATION = gql`
  mutation deleteProfileMedia($mediaId: Float!, $profileId: Float!) {
    deleteProfileMedia(mediaId: $mediaId, profileId: $profileId)
  }
`;

export const UPDATE_PROFILE_MEDIA_LIST_MUTATION = gql`
  mutation updateProfileMediaList($profileMediaList: [ProfileMediaInput!]!) {
    updateProfileMediaList(profileMediaList: $profileMediaList)
  }
`;

export const ATTACH_MEDIAS_TO_PROFILE_MUTATION = gql`
  mutation attachMediasToProfile($mediaIds: [Int!]!, $profileId: Int!) {
    attachMediasToProfile(mediaIds: $mediaIds, profileId: $profileId)
  }
`;

export const GET_ACCOUNT_DETAILS_INFO = gql`
  query getComplexAccountInfo($ids: Float!) {
    getComplexAccountInfo(accountId: $ids) {
      firstName
      id
      lastName
      countryId
      country {
        id
        code
      }
      artists {
        minor
        artistId
        profiles {
          profileId
          stageName
          dateOfBirth
          profileStatus {
            id
            code
            name
          }
        }
      }
    }
  }
`;

export const GET_UPDATED_PROFILE_FIELD = gql`
  {
    updatedProfileField @client
  }
`;

export const SET_UPDATED_PROFILE_FIELD = gql`
  mutation setUpdatedProfileField($updatedProfileField: String!) {
    setUpdatedProfileField(updatedProfileField: $updatedProfileField) @client
  }
`;

export const GET_ALL_CONFLICTS = gql`
  query conflicts {
    conflicts {
      id
      code
      name
      selectable
      parentObject {
        id
        name
      }
      childObjects {
        id
        name
      }
    }
  }
`;

export const GET_SELECTED_DIVISION = gql`
  query organizations($id: ID!) {
    organizations(ids: [$id]) {
      id
      name
      parentOrganization {
        id
        name
      }
    }
  }
`;

export const GET_CREATE_PROFILE_UPLOADED_PHOTO = gql`
  {
    createProfileUploadedPhoto @client {
      ...BaseMediaWithThumbnail
    }
  }
  ${MediaQueries.baseMediaWithThumbnailFragment}
`;

export const EDIT_CREATE_PROFILE_UPLOADED_PHOTO = gql`
  mutation EDIT_CREATE_PROFILE_UPLOADED_PHOTO($media: Media) {
    editCreateProfileUploadedPhoto(media: $media) @client
  }
`;
